import { ampli } from '@/ampli';
import { parseUnits } from 'viem';
import { getBytecodeQueryKey } from 'wagmi/query';

import { ClientInputError, ClientWalletError } from '@/lib/errors/client';
import { logger } from '@/lib/logger';

import {
  getFailureReason,
  getGaslessFailureReason,
} from '@/components/TransactionModule/MarketMode/SubmitTrade/utils';
import { submitIntentsBatch } from '@/components/TransactionModule/submitIntentsBatch';
import { signIntentsTrade, trackRwaSwapInitiated } from '@/components/TransactionModule/utils';

import { SUPPORTED_CHAINS_BY_CHAIN_ID } from '@/constants/chain';

import type { Price, Quote } from '@/utils/0x.types';
import { trackError } from '@/utils/errors';
import type { IntentsQuote } from '@/utils/intents/types';
import type { EthereumAddress } from '@/utils/models';
import { logReferralToSpindl } from '@/utils/referrals';
import { routes } from '@/utils/routes';
import { getSubmissionReason } from '@/utils/submissionReason';
import { trackTransactionFailed } from '@/utils/transactionAnalytics';
import {
  getPrivyWalletClient,
  isUserRejection,
  reportWalletGuardDiagnostics,
} from '@/utils/wallet';

import type { FailureEvent } from '../analytics';
import { buildTokenEventFields, matchaReferrer, slippageFromQuote } from '../analytics';
import type { ExecutionContext, PreflightResult, SubmitResult, TradeStrategy } from '../types';

type ExecutableIntentsQuote = IntentsQuote & { taker: EthereumAddress };
type PreparedIntentsTrade = PreflightResult & {
  raw: Price | Quote | undefined;
};

function marketFailure(
  ctx: ExecutionContext,
  raw: Price | Quote | undefined,
  details: Pick<FailureEvent, 'failureStage' | 'failureReason' | 'submissionReason'>,
): FailureEvent {
  const slippage = raw ? slippageFromQuote(raw) : { bps: undefined, mode: undefined };
  return {
    sellToken: ctx.sellToken,
    buyToken: ctx.buyToken,
    swapOrderType: 'market',
    swapExecutionType: 'gasless',
    swapSlippageBps: slippage.bps,
    swapSlippageMode: slippage.mode,
    matchaReferrer: matchaReferrer(ctx),
    recipientAddress: ctx.recipientAddress,
    ...details,
  };
}

function postTradeSubmit(ctx: ExecutionContext, quote: ExecutableIntentsQuote, hash: string) {
  if (ctx.referrer.id) {
    if (ctx.referrer.isExpired()) {
      ctx.referrer.clear();
    } else if (ctx.accountAddress) {
      void logReferralToSpindl({
        address: ctx.accountAddress,
        chainId: ctx.chainId.toString(),
        isGasless: true,
        quote,
        txnHash: hash,
        buyTokenDecimals: ctx.buyToken.decimals,
        sellTokenDecimals: ctx.sellToken.decimals,
        tokenUsdPrices: ctx.tokenUsdPrices,
        referrer: ctx.referrer.id,
        status: 'pending',
      });
    }
  }
  trackRwaSwapInitiated({
    sellToken: ctx.sellToken,
    buyToken: ctx.buyToken,
    orderType: 'market',
    transactionHash: hash,
  });
  if (ctx.accountAddress) {
    void fetch(routes.api.TRADE_SUBMIT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        walletAddress: ctx.accountAddress,
        tokenAddress: ctx.buyToken.address,
        chainId: ctx.chainId,
      }),
    })
      .then((response) => {
        if (!response.ok) logger.warn('[Trade Submit] API error:', response.status);
      })
      .catch((error: unknown) => {
        logger.warn('[Trade Submit] Failed to log recommendation trade:', error);
      });
  }
  ctx.deps.resetInputs();
}

/** Matcha Auto strategy for signing and submitting intents-native trades. */
export const intentsStrategy: TradeStrategy<PreparedIntentsTrade> = {
  lane: 'intents',
  preflight: (ctx) =>
    ctx.quote.source === 'intents' && ctx.ethereumWallet
      ? { chainFamily: 'evm', raw: ctx.quote.raw }
      : null,
  submit: async (ctx, prepared) => {
    ampli.swapInitiated({
      ...buildTokenEventFields({
        source: 'input',
        sellToken: ctx.sellToken,
        buyToken: ctx.buyToken,
        sellInput: ctx.sellInput,
        buyInput: ctx.buyInput,
      }),
      matcha_referrer: matchaReferrer(ctx),
      recipient_address: ctx.recipientAddress,
      swap_order_type: 'market',
      swap_execution_type: 'gasless',
    });

    const fail = async (rejected: boolean, error: Error): Promise<SubmitResult> => {
      await ctx.deps.invalidateTradeQuotes();
      return { success: false, rejected, error };
    };

    if (
      !prepared.raw ||
      !('allowanceTarget' in prepared.raw) ||
      !('trade' in prepared.raw) ||
      !prepared.raw.trade ||
      !('taker' in prepared.raw)
    ) {
      const error = new ClientInputError('cant submit intents order: no quote');
      trackError(error);
      return fail(false, error);
    }
    const quote: ExecutableIntentsQuote = prepared.raw;

    try {
      if (quote.taker !== ctx.accountAddress) throw new Error('taker mismatch');

      const chain = SUPPORTED_CHAINS_BY_CHAIN_ID[ctx.chainId];
      const client = chain ? await getPrivyWalletClient(ctx.ethereumWallet, chain) : undefined;
      if (!client || !ctx.publicClient) {
        reportWalletGuardDiagnostics({
          ethereumWallet: ctx.ethereumWallet,
          targetChainId: ctx.sellToken.chainId,
          accountAddress: ctx.accountAddress,
          walletClientSource: 'privy_direct',
        });
        const error = new ClientWalletError('Wallet not connected');
        trackTransactionFailed(
          marketFailure(ctx, quote, {
            failureStage: 'pre_submission',
            failureReason: 'wallet_not_ready',
          }),
        );
        trackError(error);
        return fail(false, error);
      }
      const code = await ctx.publicClient.getCode({ address: quote.taker });
      const signerSupportsIntentsApproval = !code || code === '0x';
      if (signerSupportsIntentsApproval && quote.approval && !ctx.signedApproval) {
        // Refresh the UI's signer check so the missing permit step can appear.
        ctx.deps.invalidateQueries(
          getBytecodeQueryKey({ address: quote.taker, chainId: ctx.chainId }),
        );
        throw new ClientWalletError('Approval signature required');
      }

      let signedTrade;
      try {
        signedTrade = await signIntentsTrade({
          type: quote.trade.type,
          eip712: quote.trade.eip712,
          walletClient: client,
          useRawSignature: !signerSupportsIntentsApproval,
        });
      } catch (error: unknown) {
        const rejected = isUserRejection(error);
        const normalizedError = error instanceof Error ? error : new Error(String(error));
        trackTransactionFailed(
          marketFailure(ctx, quote, {
            failureStage: 'submission',
            failureReason: rejected ? 'user_rejected' : getFailureReason(normalizedError),
            submissionReason: rejected ? undefined : getSubmissionReason(normalizedError),
          }),
        );
        if (!rejected) trackError(error);
        return fail(rejected, normalizedError);
      }

      if (!ctx.accountAddress) {
        return fail(false, new ClientWalletError('no connected wallet address'));
      }

      const approval =
        !signerSupportsIntentsApproval || !quote.approval ? undefined : ctx.signedApproval;
      let response;
      try {
        response = await submitIntentsBatch({ trade: signedTrade, approval }, ctx.chainId, {
          buy_token_address: ctx.buyToken.address,
          buy_token_amount: parseUnits(quote.buyAmount, ctx.buyToken.decimals).toString(),
          buy_token_symbol: ctx.buyToken.symbol,
          chain_id: ctx.chainId,
          order_type: 'market_auto',
          sell_token_address: ctx.sellToken.address,
          sell_token_amount: parseUnits(quote.sellAmount, ctx.sellToken.decimals).toString(),
          sell_token_symbol: ctx.sellToken.symbol,
          usd_dollar_amount: ctx.tokenUsdPrices.buyTokenUsd,
          wallet_connected_address: ctx.accountAddress,
        });
      } catch (error: unknown) {
        const rejected = isUserRejection(error);
        const normalizedError = error instanceof Error ? error : new Error(String(error));
        trackTransactionFailed(
          marketFailure(ctx, quote, {
            failureStage: 'submission',
            failureReason: rejected ? 'user_rejected' : getGaslessFailureReason(normalizedError),
            submissionReason: rejected ? undefined : getSubmissionReason(normalizedError),
          }),
        );
        return fail(rejected, normalizedError);
      }

      const tradeHash = response.tradeHash;
      const tokenFields = buildTokenEventFields({
        source: 'quote',
        sellToken: ctx.sellToken,
        buyToken: ctx.buyToken,
        sellAmount: quote.sellAmount,
        buyAmount: quote.buyAmount,
        tokenUsdPrices: ctx.tokenUsdPrices,
      });
      const slippage = slippageFromQuote(quote);
      ctx.deps.addTransaction(
        {
          sellToken: ctx.sellToken,
          sellAmount: quote.sellAmount,
          sellTokenUsdPrice: ctx.tokenUsdPrices.sellTokenUsd,
          sellAmountUsd: tokenFields.sell_token_amount_usd,
          buyToken: ctx.buyToken,
          buyAmount: quote.buyAmount,
          buyTokenUsdPrice: ctx.tokenUsdPrices.buyTokenUsd,
          buyAmountUsd: tokenFields.buy_token_amount_usd,
          feeSource: quote.fees.zeroExFee,
          referrerId: matchaReferrer(ctx),
          hash: tradeHash,
          raw: response,
          timestamp: new Date().toISOString(),
          transactionType: 'intents',
          recipient: ctx.recipientAddress,
          slippageBps: slippage.bps,
          slippageMode: slippage.mode,
        },
        ctx.accountAddress,
        ctx.chainId,
      );
      ampli.transactionSubmitted({
        ...tokenFields,
        is_smart_wallet: ctx.isSmartWallet,
        matcha_referrer: matchaReferrer(ctx),
        recipient_address: ctx.recipientAddress,
        swap_order_type: 'market',
        swap_execution_type: 'gasless',
        swap_slippage_bps: slippage.bps,
        swap_slippage_mode: slippage.mode,
        swap_tx_hash: tradeHash,
      });
      postTradeSubmit(ctx, quote, tradeHash);
      return { success: true, outcome: { kind: 'tradeHash', value: tradeHash } };
    } catch (error: unknown) {
      const rejected = isUserRejection(error);
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      trackTransactionFailed(
        marketFailure(ctx, quote, {
          failureStage: 'submission',
          failureReason: rejected ? 'user_rejected' : getGaslessFailureReason(normalizedError),
          submissionReason: rejected ? undefined : getSubmissionReason(normalizedError),
        }),
      );
      if (!rejected) trackError(error);
      return fail(rejected, normalizedError);
    }
  },
};
